import * as RadixToast from "@radix-ui/react-toast"
import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type MouseEventHandler,
  type ReactNode,
} from "react"
import { Button } from "./button"
import { Close } from "./iconOutline"
import { type SolidIcon } from "./iconCore"

export const ToastRoot = RadixToast.Root
export const ToastTitle = RadixToast.Title

export type ToastProviderProps = {
  children?: ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  return (
    <RadixToast.Provider swipeDirection="up">{children}</RadixToast.Provider>
  )
}

export type ToastViewportProps = ComponentPropsWithoutRef<
  typeof RadixToast.Viewport
>

export const ToastViewport = forwardRef<HTMLOListElement, ToastViewportProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadixToast.Viewport
        className={`toast-viewport ${className ?? ""}`}
        {...props}
        ref={ref}
      />
    )
  },
)

ToastViewport.displayName = "ToastViewport"

export const variants = [
  "neutral",
  "info",
  "highlight",
  "error",
  "warning",
  "success",
] as const

// We intentionally are not exposing all of the Radix props for customization so that
// the design system can enforce specific constraints on some properties
export type ExposedRadixToastRootProps = Pick<
  ComponentPropsWithoutRef<typeof RadixToast.Root>,
  | "type"
  | "duration"
  | "open"
  | "onOpenChange"
  | "onEscapeKeyDown"
  | "onPause"
  | "onResume"
  | "onSwipeStart"
  | "onSwipeMove"
  | "onSwipeEnd"
  | "onSwipeCancel"
>

export type ToastProps = ComponentPropsWithoutRef<"li"> & {
  /**
   * Represents the main announcement in the toast. In many cases this is all that
   * is necessary to notify the user about what happened.
   *
   * Note that this is recommended to be no longer than one line.
   */
  toastTitle: ReactNode

  /**
   * Optionally provide a description if the toastTitle does not provide
   * sufficient information. Ideally this should also be short and easy to digest
   * but unlike the toastTitle it is acceptable for this to be several short
   * sentences.
   */
  toastDescription?: ReactNode

  /**
   * Optionally provide an action (or actions) that the user can take.
   */
  toastAction?: ReactNode

  /**
   * The icon to display in the FeaturedIcon component. Note that this is provided
   * as a prop intentionally to restrict the range of sizes that can be used.
   */
  icon: SolidIcon

  /**
   * The notification type, example "error", "warning", etc.
   *
   * @default neutral
   */
  "data-variant"?: (typeof variants)[number]

  /**
   * Optionally provide an onClose handler. When provided the user can dismiss
   * the toast by clicking the "X" button. When not provided then the user is not
   * presented with an option to dismiss the toast.
   */
  onClose?: MouseEventHandler<HTMLButtonElement>
} & ExposedRadixToastRootProps

export const Toast = forwardRef<HTMLLIElement, ToastProps>(
  (
    {
      className,
      toastTitle,
      toastDescription,
      toastAction,
      // eslint-disable-next-line @typescript-eslint/no-shadow
      icon: Icon,
      onClose,
      type,
      onOpenChange,
      onEscapeKeyDown,
      onPause,
      onResume,
      onSwipeStart,
      onSwipeMove,
      onSwipeEnd,
      onSwipeCancel,
      duration,
      ...props
    },
    ref,
  ) => {
    return (
      <RadixToast.Root
        open
        className={`toast ${className ?? ""}`}
        ref={ref}
        data-has-description={!!toastDescription}
        {...props}
        type={type}
        duration={duration}
        onOpenChange={onOpenChange}
        onEscapeKeyDown={onEscapeKeyDown}
        onPause={onPause}
        onResume={onResume}
        onSwipeStart={onSwipeStart}
        onSwipeMove={onSwipeMove}
        onSwipeEnd={onSwipeEnd}
        onSwipeCancel={onSwipeCancel}
      >
        <div className="toast-icon">
          <Icon data-size="sm" />
        </div>
        <div className="toast-content">
          <ToastTitle className="toast-title title-xs">{toastTitle}</ToastTitle>
          {toastDescription && (
            <RadixToast.Description className="toast-description">
              {toastDescription}
            </RadixToast.Description>
          )}
          {toastAction && <div className="toast-actions">{toastAction}</div>}
        </div>
        {onClose && (
          <RadixToast.Close asChild>
            <Button
              iconOnly
              data-variant="tertiary-gray"
              aria-label="Close"
              onClick={onClose}
              size="sm"
              className="toast-close-button"
              icon={Close}
            />
          </RadixToast.Close>
        )}
      </RadixToast.Root>
    )
  },
)

Toast.displayName = "Toast"
